import React, { ReactElement, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Collapse, List, ListItem, ListItemIcon, ListItemText, Popover } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

export interface ISubListItemLinkProps {
  to: string;
  text: React.ReactNode;
  selected?: boolean;
  onClick?: () => void;
}

export interface IListItemLinkProps {
  to: string;
  text: React.ReactNode;
  icon?: ReactElement;
  selected?: boolean;
  onClick?: () => void;
  subItems?: ISubListItemLinkProps[];
  sidebarOpen?: boolean;
}

const useStyles = makeStyles((theme) => ({
  subItem: {
    // Aligns with the parent item's text, which sits after the 56px icon column.
    paddingLeft: `calc(56px + ${theme.spacing(2)})`,
    '& .MuiListItemText-primary': {
      color: theme.palette.primary.dark,
    },
  },
  subItemSelected: {
    '& .MuiListItemText-primary': {
      fontWeight: theme.typography.fontWeightBold,
    },
  },
  flyoutItem: {
    paddingRight: theme.spacing(4),
    '& .MuiListItemText-primary': {
      color: theme.palette.primary.dark,
      whiteSpace: 'nowrap',
    },
  },
  flyoutPaper: {
    overflow: 'visible',
    marginLeft: 4,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: theme.spacing(3),
      left: -8,
      width: 0,
      height: 0,
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
      borderRight: `8px solid ${theme.palette.background.paper}`,
    },
  },
}));

export const ListItemLink = (props: IListItemLinkProps) => {
  const { to, text, icon, selected, onClick, subItems, sidebarOpen = true } = props;
  const classes = useStyles();
  const hasSubItems = !!subItems && subItems.length > 0;
  const [open, setOpen] = useState(true);
  const [flyoutAnchor, setFlyoutAnchor] = useState<HTMLElement | null>(null);
  const closeTimeout = useRef<ReturnType<typeof setTimeout>>();
  const iconRef = useRef<HTMLDivElement>(null);

  const openFlyout = (event: React.MouseEvent<HTMLElement>) => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
    }
    setFlyoutAnchor(iconRef.current ?? event.currentTarget);
  };

  const scheduleFlyoutClose = () => {
    closeTimeout.current = setTimeout(() => setFlyoutAnchor(null), 150);
  };

  const cancelFlyoutClose = () => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
    }
  };

  const handleParentClick = () => {
    if (sidebarOpen) {
      setOpen((prev) => !prev);
    }
  };

  return (
    <>
      {hasSubItems ? (
        <ListItem
          button
          selected={selected}
          onClick={handleParentClick}
          onMouseEnter={!sidebarOpen ? openFlyout : undefined}
          onMouseLeave={!sidebarOpen ? scheduleFlyoutClose : undefined}
        >
          {icon && <ListItemIcon ref={iconRef}>{icon}</ListItemIcon>}
          <ListItemText>{text}</ListItemText>
          {sidebarOpen && (open ? <ExpandLess /> : <ExpandMore />)}
        </ListItem>
      ) : (
        <ListItem button component={Link} to={to} selected={selected} onClick={onClick}>
          {icon && <ListItemIcon>{icon}</ListItemIcon>}
          <ListItemText>{text}</ListItemText>
        </ListItem>
      )}
      {hasSubItems && sidebarOpen && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {subItems!.map((subItem, index) => (
              <ListItem
                key={index}
                button
                component={Link}
                to={subItem.to}
                onClick={subItem.onClick}
                className={clsx(classes.subItem, subItem.selected && classes.subItemSelected)}
              >
                <ListItemText>{subItem.text}</ListItemText>
              </ListItem>
            ))}
          </List>
        </Collapse>
      )}
      {hasSubItems && !sidebarOpen && (
        <Popover
          open={!!flyoutAnchor}
          anchorEl={flyoutAnchor}
          onClose={() => setFlyoutAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          disableScrollLock
          hideBackdrop
          style={{ pointerEvents: 'none' }}
          PaperProps={{
            className: classes.flyoutPaper,
            style: { pointerEvents: 'auto' },
            onMouseEnter: cancelFlyoutClose,
            onMouseLeave: scheduleFlyoutClose,
          }}
        >
          <List component="div" disablePadding>
            {subItems!.map((subItem, index) => (
              <ListItem
                key={index}
                button
                component={Link}
                to={subItem.to}
                onClick={() => {
                  subItem.onClick?.();
                  setFlyoutAnchor(null);
                }}
                className={clsx(classes.flyoutItem, subItem.selected && classes.subItemSelected)}
              >
                <ListItemText>{subItem.text}</ListItemText>
              </ListItem>
            ))}
          </List>
        </Popover>
      )}
    </>
  );
};
